import { useEffect, useRef, useState } from 'react';

/**
 * True once the element has scrolled into view — and stays true afterward.
 *
 * Deliberately one-way: an element that scrolls back out and into view again
 * does not replay the animation. A page that re-fades its own headlines every
 * time the reader scrolls up reads as busy and mechanical, not calm —
 * "contemplative motion" per the brief means it happens once, on arrival.
 *
 * `prefers-reduced-motion` isn't handled here — the element is still marked
 * visible (so content is never gated behind JS), and it's the `.reveal` CSS
 * itself that drops the transition under that media query.
 */
export function useReveal(options) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || visible || typeof IntersectionObserver === 'undefined') {
      if (!node && !visible) return undefined;
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px', ...options }
    );
    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return [ref, visible];
}
